import React, { useEffect, useState } from 'react';
import { Alert, Col, FormControl, Row } from 'react-bootstrap';
import { AlertTriangle, Info, RefreshCw } from 'lucide-react';
import moment from 'moment';
import AppModal from '../../../../common/AppModal';
import { getFullAddress } from '../../../../../helpers/global';
import {
  formatVersionDate,
  getNextUpdateNumber,
  getValidThrough,
  isValidThroughExpired
} from '../../../../../helpers/serviceVersionHelpers';

const UPDATE_REASONS = ['Closing date moved', 'Report needs to be refreshed', 'Other (specify in notes)'];

const labelStyle = { color: '#495057', fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 };
const inputStyle = {
  backgroundColor: '#FFFFFF',
  padding: '6px 12px',
  fontSize: 13,
  height: 36,
  lineHeight: 1.4,
  borderRadius: 10,
  border: '1px solid #DEE2E6'
};

const RequestUpdateModal = ({ show, onHide, onSubmit, submitting = false, service, order, versions = [] }) => {
  const [values, setValues] = useState({ newClosingDate: '', reason: '', notes: '' });

  const nextUpdateNo = getNextUpdateNumber(versions);

  const validThrough = getValidThrough(service?.closingDate);
  const isExpired = isValidThroughExpired(validThrough);

  useEffect(() => {
    if (!show) return;
    setValues({
      newClosingDate: service?.closingDate ? moment(service.closingDate).format('YYYY-MM-DD') : '',
      reason: '',
      notes: ''
    });
  }, [show, service?.closingDate]);

  const onFieldChange = (key, value) => setValues(prev => ({ ...prev, [key]: value }));

  return (
    <AppModal
      show={show}
      onHide={onHide}
      onSubmit={() => onSubmit?.(values)}
      dialogClassName="request-update-modal"
      submitting={submitting}
      submitDisabled={!values.newClosingDate || !values.reason}
      submitButtonVariant="warning"
      heading={
        <span className="d-flex align-items-center" style={{ color: '#212529', fontSize: 16, fontWeight: 600 }}>
          <RefreshCw size={16} className="mr-2 text-info" />
          Request Update
        </span>
      }
      submitLabel={
        <span className="d-flex align-items-center">
          <RefreshCw size={14} className="mr-1" />
          Create Update
        </span>
      }
    >
      <div className="border rounded-lg" style={{ backgroundColor: '#F8F9FA', padding: 12, marginBottom: 16 }}>
        <div style={{ color: '#ADB5BD', fontSize: 11, marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Creating update for
        </div>
        <div className="font-weight-bold" style={{ color: '#212529', fontSize: 14 }}>
          {getFullAddress(service)}
        </div>
        <div style={{ color: '#868E96', fontSize: 12, marginTop: 2, marginBottom: 12 }}>
          Folio: {service?.folioNumbers?.join(', ') || 'N/A'} · Service: {order?.orderType || 'N/A'}
          {order?.accurateId ? ` · ${order.accurateId}` : ''}
        </div>
        <Row className="mx-0">
          <Col xs={4} className="px-0 pr-2">
            <div style={{ color: '#ADB5BD', fontSize: 11 }}>Delivered</div>
            <strong style={{ color: '#212529', fontSize: 11 }}>{formatVersionDate(order?.dateDelivered)}</strong>
          </Col>
          <Col xs={4} className="px-0 pr-2">
            <div style={{ color: '#ADB5BD', fontSize: 11 }}>Original closing</div>
            <strong style={{ color: '#212529', fontSize: 11 }}>{formatVersionDate(service?.closingDate)}</strong>
          </Col>
          <Col xs={4} className="px-0">
            <div style={{ fontSize: 11 }}>
              <span style={{ color: '#ADB5BD' }}>Valid through:&nbsp;</span>
              <strong style={{ color: isExpired ? '#E53E3E' : '#212529' }}>
                {validThrough ? validThrough.format('MM/DD/YYYY') : 'N/A'}
              </strong>
            </div>
            {isExpired && <strong style={{ color: '#E53E3E', fontSize: 11 }}>(expired)</strong>}
          </Col>
        </Row>
      </div>

      <Alert
        className="d-flex align-items-center mb-3"
        style={{
          backgroundColor: '#FFF3E0',
          borderColor: '#FFE0B2',
          color: '#E65100',
          fontSize: 12,
          padding: 10
        }}
      >
        <span className="mb-0">
          <AlertTriangle size={12} className="mr-1 flex-shrink-0" style={{ color: '#E65100' }} />A new update will be
          created and <strong>all sections will be fully re-searched</strong>. A new folder named &apos;Update{' '}
          {nextUpdateNo}&apos; will be created inside the property folder.
        </span>
      </Alert>

      <Row className="mb-3">
        <Col xs={6} className="pr-2">
          <label style={labelStyle}>New Closing Date</label>
          <FormControl
            type="date"
            size="sm"
            style={inputStyle}
            value={values.newClosingDate}
            onChange={e => onFieldChange('newClosingDate', e.target.value)}
            disabled={submitting}
          />
        </Col>
        <Col xs={6} className="pl-2">
          <label style={labelStyle}>Reason for Update</label>
          <FormControl
            as="select"
            size="sm"
            style={inputStyle}
            value={values.reason}
            onChange={e => onFieldChange('reason', e.target.value)}
            disabled={submitting}
          >
            <option value="">Select a reason...</option>
            {UPDATE_REASONS.map(reason => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </FormControl>
        </Col>
      </Row>

      <div className="mb-0">
        <label style={labelStyle}>Notes</label>
        <FormControl
          size="sm"
          style={inputStyle}
          placeholder="Additional context for the processor"
          aria-label="Additional context for the processor"
          value={values.notes}
          onChange={e => onFieldChange('notes', e.target.value)}
          disabled={submitting}
        />
      </div>

      <Alert
        className="d-flex align-items-start mb-0"
        style={{
          backgroundColor: '#E8F5E9',
          borderColor: '#C8E6C9',
          color: '#2E7D32',
          fontSize: 12,
          padding: 10,
          marginTop: 14
        }}
      >
        <Info size={15} className="mr-2 flex-shrink-0" style={{ color: '#2E7D32', marginTop: 2 }} />
        <span className="mb-0" style={{ lineHeight: 1.45 }}>
          <strong>What happens:</strong> A new update (Update {nextUpdateNo}) is created with its own folder inside the
          property folder. The client comment is logged for audit trail. The exported report will include an invoice
          summary listing all invoices (Original + Updates).
        </span>
      </Alert>
    </AppModal>
  );
};

export default RequestUpdateModal;
